import { forwardRef, type ComponentPropsWithoutRef } from "react";
// eslint-disable-next-line no-restricted-imports -- only for its plain header, body and actions
import { Modal } from "react-daisyui";
import { classNames } from "src/core/utilities/classNames";

type DialogName =
    { "aria-labelledby": string; "aria-label"?: never } | { "aria-label": string; "aria-labelledby"?: never };

type Props = Omit<ComponentPropsWithoutRef<"dialog">, "aria-hidden" | "aria-label" | "aria-labelledby"> &
    DialogName & {
        /** From the bottom on a phone, in the middle from `sm` up */
        responsive?: boolean;

        /** Closes on a click outside the box */
        backdrop?: boolean;
    };

/**
 * react-daisyui's `Modal` with the same markup and classes, so daisyUI styles and animates it the
 * same. That one is `aria-hidden` unless given `open`, which a dialog opened with `showModal()`
 * never is, and is always named "Modal". This takes its name from its heading instead. A closed one
 * is kept out of reach by `dialog.modal:not([open])` in `index.css`.
 *
 * `className` goes on the box, as it did on `Modal`.
 */
const DialogWithRef = forwardRef<HTMLDialogElement, Props>(
    ({ children, open, responsive, backdrop, className, ...props }, ref) => (
        <dialog
            {...props}
            ref={ref}
            open={open}
            aria-modal={open}
            className={classNames("modal", { "modal-open": open, "modal-bottom sm:modal-middle": responsive })}
        >
            <div className={classNames("modal-box", className)}>{children}</div>

            {backdrop && (
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            )}
        </dialog>
    )
);

DialogWithRef.displayName = "Dialog";

export const Dialog = Object.assign(DialogWithRef, {
    Header: Modal.Header,
    Body: Modal.Body,
    Actions: Modal.Actions,
});
